import React from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../../components/ui/FormField';
import type { SchemaFieldProps } from '../types';

export type MembershipType = 'season' | 'calendar' | 'time' | 'entry';

/**
 * Custom SchemaForm widget for the membership `type` enum field. Reproduces the
 * former MembershipForm radio-card selector (calendar / time / entry) verbatim,
 * including the existing `.radio-cards` / `.radio-card` styling. Selection is a
 * clickable card (role="radio") rather than a visually-hidden native radio, so
 * no raw form controls are used while keeping the identical visual language.
 *
 * The offered types are filtered by `field.enumValues` when present, so a caller
 * can narrow the set (e.g. a course option hides `calendar`, which is scoped by
 * the course term rather than its own intervals). When `enumValues` is absent
 * all three are shown, preserving the standalone behaviour.
 */
const MembershipTypeField: React.FC<SchemaFieldProps> = ({ field, value, onChange, error }) => {
  const { t } = useTranslation();

  const allCards: Array<{ type: MembershipType; labelKey: string; descKey: string }> = [
    { type: 'season', labelKey: 'memberships.typeSeason', descKey: 'memberships.typeSeasonDesc' },
    { type: 'calendar', labelKey: 'memberships.typeCalendar', descKey: 'memberships.typeCalendarDesc' },
    { type: 'time', labelKey: 'memberships.typeTime', descKey: 'memberships.typeTimeDesc' },
    { type: 'entry', labelKey: 'memberships.typeEntry', descKey: 'memberships.entryCountHelp' },
  ];
  const cards =
    field.enumValues && field.enumValues.length > 0
      ? allCards.filter((c) => field.enumValues!.includes(c.type))
      : allCards;

  const fallback: MembershipType = cards[0]?.type ?? 'season';
  const selected: MembershipType = (value as MembershipType) ?? fallback;

  return (
    <FormField htmlFor="sf-membershipType" label={t('memberships.typeLabel')} required={field.required} error={error}>
      <div className="radio-cards" id="sf-membershipType" role="radiogroup">
        {cards.map((card) => {
          const isSelected = selected === card.type;
          return (
            <div
              key={card.type}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              className={`radio-card${isSelected ? ' selected' : ''}`}
              onClick={() => onChange(card.type)}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  onChange(card.type);
                }
              }}
            >
              <div>
                <div className="radio-card__label">{t(card.labelKey)}</div>
                <div className="radio-card__desc">{t(card.descKey)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </FormField>
  );
};

export default MembershipTypeField;
